import React, { useState, useEffect, forwardRef } from "react";
import DatePicker from "react-datepicker";
import { Calendar } from "lucide-react";
import {
  getPasswordStrength,
  validatePasswordComplexity,
  sanitizeInput,
} from "../../../../utils/securityUtils";

const SignupForm = ({
  title,
  countries,
  states,
  formData,
  errors,
  updateFormField,
}) => {
  const nativeSelectClass =
    "bg-white appearance-none leading-normal min-h-[42px] xl:min-h-[2.85vw] pr-10";
  const nativeSelectStyle = {
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none'%3E%3Cpath d='M5 7.5L10 12.5L15 7.5' stroke='%23002C5F' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 0.75rem center",
    backgroundSize: "0.95rem 0.95rem",
  };
  const [passwordStrength, setPasswordStrength] = useState({
    strength: "weak",
    score: 0,
  });
  const [passwordHints, setPasswordHints] = useState([]);
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);

  // Real-time password validation
  useEffect(() => {
    if (formData.password) {
      const strength = getPasswordStrength(formData.password);
      setPasswordStrength(strength);

      const validation = validatePasswordComplexity(formData.password);
      setPasswordHints(validation.errors);
    } else {
      setPasswordStrength({ strength: "weak", score: 0 });
      setPasswordHints([]);
    }
  }, [formData.password]);

  const getStrengthColor = (strength) => {
    switch (strength) {
      case "weak":
        return "bg-red-500";
      case "medium":
        return "bg-yellow-500";
      case "strong":
        return "bg-green-500";
      default:
        return "bg-gray-300";
    }
  };

  const getStrengthWidth = (score) => {
    return `${(score / 10) * 100}%`;
  };

  const handleInputChange = (fieldName, value, type = "text") => {
    // Sanitize input based on field type
    const sanitized = sanitizeInput(value, type);
    updateFormField(fieldName, sanitized);
  };

  const DateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
    <div className="relative w-full cursor-pointer" onClick={onClick} ref={ref}>
      <input
        value={value || ""}
        readOnly
        placeholder={placeholder}
        className="w-full border border-primary rounded-[0.5vw] p-[1vw] pr-[3vw]
        text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary"
      />
      <Calendar
        className="absolute right-[1vw] top-1/2 -translate-y-1/2
        text-primary w-[4vw] md:w-[2.5vw] xl:w-[1.2vw]"
      />
    </div>
  ));

  return (
    <div>
      <form className="w-full max-w-[80vw] mx-auto mt-[2vw]">
        {/* Row 1: Title, First Name, Last Name */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-[2vw]">
          {/* Title */}
          <div className="flex flex-col">
            <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
              Title*
            </label>
            <select
              onChange={(e) => updateFormField("title", e.target.value)}
              value={formData.title}
              className={`border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary ${nativeSelectClass}`}
              style={nativeSelectStyle}
            >
              <option value="">Select Title</option>
              {title &&
                title?.map((el, ind) => (
                  <option key={el.id} value={`${el.id}-${el.name}`}>
                    {el.name}
                  </option>
                ))}
            </select>
            {errors.title && (
              <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
                {errors.title}
              </p>
            )}
          </div>

          {/* First Name */}
          <div className="flex flex-col">
            <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
              First Name*
            </label>
            <input
              value={formData.firstName}
              onChange={(e) =>
                handleInputChange("firstName", e.target.value, "name")
              }
              type="text"
              maxLength={50}
              className="border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary"
              placeholder="Enter first name"
            />
            {errors.firstName && (
              <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
                {errors.firstName}
              </p>
            )}
          </div>

          {/* Last Name */}
          <div className="flex flex-col">
            <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
              Last Name*
            </label>
            <input
              value={formData.lastName}
              onChange={(e) =>
                handleInputChange("lastName", e.target.value, "name")
              }
              type="text"
              maxLength={50}
              className="border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary"
              placeholder="Enter last name"
            />
            {errors.lastName && (
              <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
                {errors.lastName}
              </p>
            )}
          </div>
        </div>

        {/* Row 2: Gender, DOB, Mobile */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-[2vw] mt-[2vw]">
          {/* Gender */}
          <div className="flex flex-col">
            <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
              Gender*
            </label>
            <select
              value={formData.gender}
              onChange={(e) => updateFormField("gender", e.target.value)}
              className={`border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary ${nativeSelectClass}`}
              style={nativeSelectStyle}
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
            {errors.gender && (
              <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
                {errors.gender}
              </p>
            )}
          </div>

          {/* Date of Birth */}
          <div className="flex flex-col">
            <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
              Date of Birth*
            </label>
            <DatePicker
              selected={
                formData.dateOfBirth
                  ? new Date(
                      formData.dateOfBirth.split("-").reverse().join("-")
                    )
                  : null
              }
              onChange={(date) =>
                updateFormField(
                  "dateOfBirth",
                  date
                    ? date.toLocaleDateString("en-GB").replaceAll("/", "-")
                    : ""
                )
              }
              dateFormat="dd-MM-yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              maxDate={new Date()}
              placeholderText="Select DOB"
              customInput={<DateInput placeholder="Select DOB" />}
              className="w-[100%] border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary"
            />
            {errors.dateOfBirth && (
              <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
                {errors.dateOfBirth}
              </p>
            )}
          </div>

          {/* Mobile */}
          <div className="flex flex-col">
            <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
              Mobile*
            </label>
            <div className="flex items-center border border-gray-400 rounded-[0.5vw] overflow-hidden">
              {/* Country Code Select */}
              <select
                value={formData.phone_code}
                onChange={(e) => updateFormField("phoneCode", e.target.value)}
                className={`px-[0.5vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1vw] bg-gray-100 border-r border-gray-400 outline-none text-primary ${nativeSelectClass}`}
                defaultValue="+91"
                style={nativeSelectStyle}
              >
                <option value="+91">+91 (IN)</option>
                {countries &&
                  countries?.map((el, ind) => (
                    <option key={ind} value={el.phone_code}>
                      {el.phone_code}
                    </option>
                  ))}
              </select>

              {/* Mobile Input */}
              <input
                value={formData.mobile}
                onChange={(e) => updateFormField("mobile", e.target.value)}
                type="tel"
                className="flex-1 p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1vw] outline-none text-primary"
                placeholder="Enter mobile number"
              />
            </div>
            {errors.mobile && (
              <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
                {errors.mobile}
              </p>
            )}
          </div>
        </div>

        {/* Row 3: Country, State, City */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-[2vw] mt-[2vw]">
          <div className="flex flex-col">
            <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
              Country*
            </label>
            <select
              value={formData.country}
              onChange={(e) => updateFormField("country", e.target.value)}
              className={`border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary ${nativeSelectClass}`}
              style={nativeSelectStyle}
            >
              <option value="">Select Country</option>
              {countries &&
                countries?.map((el, ind) => (
                  <option key={ind} value={el.id}>
                    {el.name}
                  </option>
                ))}
            </select>
            {errors.country && (
              <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
                {errors.country}
              </p>
            )}
          </div>
          <div className="flex flex-col">
            <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
              State*
            </label>
            {states && states.length > 0 ? (
              <select
                value={formData.state}
                onChange={(e) => updateFormField("state", e.target.value)}
                className={`border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary ${nativeSelectClass}`}
                style={nativeSelectStyle}
              >
                <option value="">Select State</option>
                {states &&
                  states?.map((el, ind) => (
                    <option key={ind} value={el.id}>
                      {el.name}
                    </option>
                  ))}
              </select>
            ) : (
              <input
                value={formData.state}
                onChange={(e) => updateFormField("state", e.target.value)}
                type="text"
                placeholder={"Enter State"}
                className="border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary"
              />
            )}
            {errors.state && (
              <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
                {errors.state}
              </p>
            )}
          </div>
          <div className="flex flex-col">
            <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
              City*
            </label>
            <input
              value={formData.city}
              // onChange={(e) => updateFormField("city", e.target.value)}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[A-Za-z\s]*$/.test(value)) {
                  updateFormField("city", value);
                }
              }}
              onPaste={(e) => {
                const pasted = e.clipboardData.getData("text");
                if (!/^[A-Za-z\s]*$/.test(pasted)) {
                  e.preventDefault();
                }
              }}
              type="text"
              placeholder={"Enter City"}
              className="border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary"
            />
            {errors.city && (
              <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
                {errors.city}
              </p>
            )}
          </div>
        </div>

        {/* Row 4: Pincode, Email */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-[2vw] mt-[2vw]">
          {/* Pincode */}
          <div className="flex flex-col">
            <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
              Pincode*
            </label>
            <input
              value={formData.pincode}
              onChange={(e) => updateFormField("pincode", e.target.value)}
              type="text"
              maxLength={10}
              placeholder="Enter pincode"
              className="border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary"
            />
            {errors.pincode && (
              <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
                {errors.pincode}
              </p>
            )}
          </div>

          {/* Email */}
          <div className="flex flex-col">
            <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
              Email ID*
            </label>
            <input
              value={formData.email}
              onChange={(e) => updateFormField("email", e.target.value)}
              type="email"
              placeholder="Enter email"
              className="border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary"
            />
            {errors.email && (
              <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
                {errors.email}
              </p>
            )}
          </div>
        </div>

        {/* Row 5: Full Address */}
        <div className="flex flex-col mt-[2vw]">
          <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
            Full Address <span className="font-redhatRegular text-[2.5vw] md:text-[1.5vw] xl:text-[0.8vw]">Allowed (# , / -)</span>
          </label>
          <textarea
            value={formData.fullAddress}
            onChange={(e) => updateFormField("fullAddress", e.target.value)}
            rows="3"
            placeholder="Enter full address"
            className="border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary"
          ></textarea>
          {errors.fullAddress && (
            <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
              {errors.fullAddress}
            </p>
          )}
        </div>

        {/* Row 6: Password, Confirm Password */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-[2vw] mt-[2vw]">
          <div className="flex flex-col">
            <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
              Password*
            </label>
            <input
              value={formData.password}
              onChange={(e) => updateFormField("password", e.target.value)}
              onFocus={() => setShowPasswordRequirements(true)}
              type="password"
              placeholder={"Enter Password"}
              className="border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary"
            />

            {/* Password Strength Indicator */}
            {formData.password && (
              <div className="mt-[0.5vw]">
                <div className="flex items-center justify-between mb-[0.3vw]">
                  <span className="text-[2.5vw] xl:text-[0.8vw] font-redhatRegular text-gray-600">
                    Password Strength:
                  </span>
                  <span
                    className={`text-[2.5vw] xl:text-[0.8vw] font-redhatMedium capitalize ${
                      passwordStrength.strength === "weak"
                        ? "text-red-500"
                        : passwordStrength.strength === "medium"
                        ? "text-yellow-600"
                        : "text-green-600"
                    }`}
                  >
                    {passwordStrength.strength}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-[0.5vw]">
                  <div
                    className={`h-[0.5vw] rounded-full transition-all duration-300 ${getStrengthColor(
                      passwordStrength.strength
                    )}`}
                    style={{ width: getStrengthWidth(passwordStrength.score) }}
                  ></div>
                </div>
              </div>
            )}

            {/* Password Requirements */}
            {showPasswordRequirements && (
              <div className="mt-[0.5vw] p-[0.8vw] bg-blue-50 border border-blue-200 rounded-[0.3vw]">
                <p className="text-[2.5vw] xl:text-[0.85vw] font-redhatMedium text-blue-800 mb-[0.3vw]">
                  Password Requirements:
                </p>
                <ul className="text-[2.3vw] xl:text-[0.75vw] text-blue-700 space-y-[0.2vw]">
                  <li className="flex items-start">
                    <span className="mr-[0.3vw]">•</span>
                    <span>At least 12 characters long</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-[0.3vw]">•</span>
                    <span>One uppercase letter (A-Z)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-[0.3vw]">•</span>
                    <span>One lowercase letter (a-z)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-[0.3vw]">•</span>
                    <span>One number (0-9)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="mr-[0.3vw]">•</span>
                    <span>One special character (!@#$%^&*...)</span>
                  </li>
                </ul>
              </div>
            )}

            {errors.password && (
              <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
                {errors.password}
              </p>
            )}
          </div>
          <div className="flex flex-col">
            <label className="font-redhatMedium text-[3.5vw] md:text-[2.5vw] xl:text-[1.2vw] mb-[0.5vw] text-primary">
              Confirm Password*
            </label>
            <input
              value={formData.confirmPassword}
              onChange={(e) =>
                updateFormField("confirmPassword", e.target.value)
              }
              type="password"
              placeholder={"Enter Confirm Password"}
              className="border border-primary rounded-[0.5vw] p-[1vw] text-[3.5vw] md:text-[2.5vw] xl:text-[1.1vw] outline-none text-primary"
            />
            {errors.confirmPassword && (
              <p className="text-red-500 text-[3.5vw] md:text-[1.5vw] xl:text-[0.9vw] font-redhatRegular">
                {errors.confirmPassword}
              </p>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default SignupForm;
